import moment from 'moment'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { applyOpacity, formatDate } from '@helpers'

import { Typography } from '@components/ui'
import { List, LIST_ITEM_CSS_VARIABLES, ListItem, ListItemIcon } from '@components/ui/List'
import { KeyboardArrowRightIcon } from '@components/ui/svgIcons'

import { DATE_WIDGET_VARIANTS as VARIANTS } from '../constants'

const StyledTypography = styled(Typography)`
  margin-bottom: 10px;
`

const StyledListItem = styled(ListItem)`
  min-height: 0;
`

const StyledList = styled(List)`
  &[data-grid='true'] {
    display: grid;
    grid-template-rows: repeat(4, 1fr);
    grid-template-columns: repeat(3, 1fr);
    grid-auto-flow: column;
    grid-row-gap: 10px;
    grid-column-gap: 20px;

    ${LIST_ITEM_CSS_VARIABLES.listItemHorizontalPadding}: 0;
    ${LIST_ITEM_CSS_VARIABLES.listItemVerticalPadding}: 0;
    ${LIST_ITEM_CSS_VARIABLES.listItemTextColor}: ${({ theme }) => theme.colors.blue[40]};
    ${LIST_ITEM_CSS_VARIABLES.menuItemHoverColor}: initial;

    ${StyledListItem} {
      font-size: 12px;
      line-height: 1.4;
      font-weight: 700;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &[data-grid='false'] {
    ${LIST_ITEM_CSS_VARIABLES.listItemHorizontalPadding}: 20px;
    ${LIST_ITEM_CSS_VARIABLES.listItemVerticalPadding}: 5px;
    ${LIST_ITEM_CSS_VARIABLES.listItemTextColor}: ${({ theme }) => theme.colors.gray[100]};
    ${LIST_ITEM_CSS_VARIABLES.menuItemHoverColor}: ${({ theme }) => applyOpacity(theme.colors.blue[40], 6)};

    ${StyledListItem} {
      height: 30px;
      display: flex;
      align-items: center;
      font-size: 14px;
      line-height: 20px;
      box-sizing: border-box;
    }
  }
`

const StyledListItemIcon = styled(ListItemIcon)`
  min-width: 0;
  margin-right: 10px;
  font-size: 14px;
  color: inherit;
`

const PERIODS = {
  previousYear: 'previousYear',
  currentYear: 'currentYear',
  previousQuarter: 'previousQuarter',
  currentQuarter: 'currentQuarter',
  previousMonth: 'previousMonth',
  currentMonth: 'currentMonth',
  previousWeek: 'previousWeek',
  currentWeek: 'currentWeek',
} as const

interface DatePeriodsMenuProps {
  disabled?: boolean
  isDateFilterToggleDisabled?: boolean
  isToggleHidden?: boolean
  onChange: (values: { fromDate: string; toDate: string }) => void
  onToggle?: (payload: boolean) => void
  variant: ConstObjectValues<typeof VARIANTS>
}

export default function DatePeriodsMenu({
  disabled = false,
  isDateFilterToggleDisabled = false,
  isToggleHidden = false,
  onChange,
  onToggle,
  variant,
}: DatePeriodsMenuProps) {
  const handleChange = (type: ConstObjectValues<typeof PERIODS>) => () => {
    let fromDate = formatDate(moment())
    let toDate = formatDate(moment())
    switch (type) {
      case PERIODS.previousYear: {
        fromDate = formatDate(moment().subtract(1, 'year').startOf('year'))
        toDate = formatDate(moment().subtract(1, 'year').endOf('year'))
        break
      }
      case PERIODS.currentYear: {
        fromDate = formatDate(moment().startOf('year'))
        toDate = formatDate(moment().endOf('year'))
        break
      }
      case PERIODS.previousQuarter: {
        const currentQuarterNumber = moment().quarter()
        if (currentQuarterNumber === 1) {
          fromDate = formatDate(moment().subtract(1, 'year').quarter(4).startOf('quarter'))
          toDate = formatDate(moment().subtract(1, 'year').endOf('year'))
        } else {
          fromDate = formatDate(
            moment()
              .quarter(currentQuarterNumber - 1)
              .startOf('quarter')
          )
          toDate = formatDate(
            moment()
              .quarter(currentQuarterNumber - 1)
              .endOf('quarter')
          )
        }
        break
      }
      case PERIODS.currentQuarter: {
        const currentQuarterNumber = moment().quarter()
        fromDate = formatDate(moment().quarter(currentQuarterNumber).startOf('quarter'))
        toDate = formatDate(
          currentQuarterNumber === 4 ? moment().endOf('year') : moment().quarter(currentQuarterNumber).endOf('quarter')
        )
        break
      }
      case PERIODS.previousMonth: {
        fromDate = formatDate(moment().subtract(1, 'month').startOf('month'))
        toDate = formatDate(moment().subtract(1, 'month').endOf('month'))
        break
      }
      case PERIODS.currentMonth: {
        fromDate = formatDate(moment().startOf('month'))
        toDate = formatDate(moment().endOf('month'))
        break
      }
      case PERIODS.previousWeek: {
        fromDate = formatDate(moment().subtract(1, 'week').startOf('week'))
        toDate = formatDate(moment().subtract(1, 'week').endOf('week'))
        break
      }
      case PERIODS.currentWeek: {
        fromDate = formatDate(moment().startOf('week'))
        toDate = formatDate(moment().endOf('week'))
        break
      }
      default:
        return
    }

    onChange({ fromDate, toDate })
  }

  function handleDateFilterToggle() {
    onToggle?.(!disabled)
  }

  return (
    <>
      {variant === VARIANTS.list && (
        <StyledTypography size="700-xs" color="gray-80" uppercase tag="h4">
          <FormattedMessage id="datePeriodsMenu.title" defaultMessage="Időszakok" />
        </StyledTypography>
      )}
      <StyledList data-grid={variant === VARIANTS.list} disablePadding={variant === VARIANTS.list} menu>
        <StyledListItem onClick={handleChange(PERIODS.currentYear)} menuItem>
          {variant === VARIANTS.list && (
            <StyledListItemIcon>
              <KeyboardArrowRightIcon size={14} />
            </StyledListItemIcon>
          )}
          <Typography>
            <FormattedMessage id="datePeriodsMenu.periods.currentYear" defaultMessage="Aktuális év" />
          </Typography>
        </StyledListItem>

        <StyledListItem onClick={handleChange(PERIODS.currentQuarter)} menuItem>
          {variant === VARIANTS.list && (
            <StyledListItemIcon>
              <KeyboardArrowRightIcon size={14} />
            </StyledListItemIcon>
          )}
          <Typography>
            <FormattedMessage id="datePeriodsMenu.periods.currentQuarter" defaultMessage="Aktuális negyedév" />
          </Typography>
        </StyledListItem>

        <StyledListItem onClick={handleChange(PERIODS.currentMonth)} menuItem>
          {variant === VARIANTS.list && (
            <StyledListItemIcon>
              <KeyboardArrowRightIcon size={14} />
            </StyledListItemIcon>
          )}
          <Typography>
            <FormattedMessage id="datePeriodsMenu.periods.currentMonth" defaultMessage="Aktuális hónap" />
          </Typography>
        </StyledListItem>

        <StyledListItem onClick={handleChange(PERIODS.currentWeek)} menuItem>
          {variant === VARIANTS.list && (
            <StyledListItemIcon>
              <KeyboardArrowRightIcon size={14} />
            </StyledListItemIcon>
          )}
          <Typography>
            <FormattedMessage id="datePeriodsMenu.periods.currentWeek" defaultMessage="Aktuális hét" />
          </Typography>
        </StyledListItem>

        <StyledListItem onClick={handleChange(PERIODS.previousYear)} menuItem>
          {variant === VARIANTS.list && (
            <StyledListItemIcon>
              <KeyboardArrowRightIcon size={14} />
            </StyledListItemIcon>
          )}
          <Typography>
            <FormattedMessage id="datePeriodsMenu.periods.previousYear" defaultMessage="Előző év" />
          </Typography>
        </StyledListItem>

        <StyledListItem onClick={handleChange(PERIODS.previousQuarter)} menuItem>
          {variant === VARIANTS.list && (
            <StyledListItemIcon>
              <KeyboardArrowRightIcon size={14} />
            </StyledListItemIcon>
          )}
          <Typography>
            <FormattedMessage id="datePeriodsMenu.periods.previousQuarter" defaultMessage="Előző negyedév" />
          </Typography>
        </StyledListItem>

        <StyledListItem onClick={handleChange(PERIODS.previousMonth)} menuItem>
          {variant === VARIANTS.list && (
            <StyledListItemIcon>
              <KeyboardArrowRightIcon size={14} />
            </StyledListItemIcon>
          )}
          <Typography>
            <FormattedMessage id="datePeriodsMenu.periods.previousMonth" defaultMessage="Előző hónap" />
          </Typography>
        </StyledListItem>

        <StyledListItem onClick={handleChange(PERIODS.previousWeek)} menuItem>
          {variant === VARIANTS.list && (
            <StyledListItemIcon>
              <KeyboardArrowRightIcon size={14} />
            </StyledListItemIcon>
          )}
          <Typography>
            <FormattedMessage id="datePeriodsMenu.periods.previousWeek" defaultMessage="Előző hét" />
          </Typography>
        </StyledListItem>

        {!isToggleHidden && (
          <StyledListItem onClick={handleDateFilterToggle} disabled={isDateFilterToggleDisabled} menuItem>
            {variant === VARIANTS.list && (
              <StyledListItemIcon>
                <KeyboardArrowRightIcon size={14} />
              </StyledListItemIcon>
            )}
            <Typography>
              {disabled ? (
                <FormattedMessage id="datePeriodsMenu.toggle.enable" defaultMessage="Dátumszűrés bekapcsolása" />
              ) : (
                <FormattedMessage id="datePeriodsMenu.toggle.disable" defaultMessage="Dátumszűrés kikapcsolása" />
              )}
            </Typography>
          </StyledListItem>
        )}
      </StyledList>
    </>
  )
}
